<template>
   <div>
      <div class="my-3">
         <BaseButton @click="emit('show-list')" color="black" variant="plain">
            <ArrowLeftIcon v-if="locale == 'en'" class="h-4 w-4 text-secondary-400" />
            <ArrowRightIcon v-else class="h-4 w-4 text-secondary-400" />
            {{ $t('shared.back') }}
         </BaseButton>
      </div>
      <div class="grid grid-cols-12 gap-4 pb-8">
         <div
            dir="ltr"
            class="border order-1 border-gray-100 border-opacity-40 sm:col-span-9 col-span-12"
         >
            <div id="invoice-content" class="bg-white col-span-12 p-6">
               <div class="flex px-4 items-start justify-between">
                  <div>
                     <div class="text:2xl sm:text-3xl font-medium uppercase mb-2 text-gray-800">
                        Invoice
                     </div>
                     <p class="text-xs text-gray-600">#{{ invoiceIdentity.header }}</p>
                  </div>
                  <div>
                     <span class="mb-2 text-sm font-medium">Invoice Date:</span>
                     <span class="text-xs ms-2 text-gray-600">
                        {{ dayjs(invoiceDetail?.date).format('DD MMMM, YYYY') }}
                     </span>
                  </div>
                  <img src="@/assets/image/logo.png" alt="checklist" width="140" />
               </div>

               <div
                  class="grid gap-2 grid-cols-12 pb-0 border-b border-gray-100 border-opacity-40 text-gray-1000 px-4 my-2 justify-between"
               >
                  <div class="pe-4 mb-3 col-span-4">
                     <div class="mb-2 text-sm font-medium">Billed To:</div>
                     <div class="text-xs text-gray-600">
                        <div>
                           <span class="font-medium text-gray-1000">Name: </span>
                           <span>{{ invoiceDetail?.client?.name }}</span>
                        </div>

                        <div class="my-2" v-if="invoiceDetail?.client?.account_id">
                           <span class="font-medium text-gray-1000">Account ID: </span>
                           <span>{{ invoiceDetail?.client?.account_id }}</span>
                        </div>
                        <div v-if="invoiceDetail?.client?.national_id" class="mb-2">
                           <span class="font-medium text-gray-1000">Emirates ID: </span>
                           <span>{{ invoiceDetail?.client?.national_id || '-' }}</span>
                        </div>
                        <div v-if="invoiceDetail?.client?.date_of_birth" class="mb-2">
                           <span class="font-medium text-gray-1000">DOB: </span>
                           <span v-if="invoiceDetail?.client?.date_of_birth">
                              {{
                                 dayjs(invoiceDetail?.client?.date_of_birth).format('DD MMMM, YYYY')
                              }}
                           </span>
                           <span v-else> - </span>
                        </div>
                        <div>
                           <span class="font-medium text-gray-1000">Country: </span>
                           <span>{{ invoiceDetail?.client?.country }}</span>
                        </div>
                     </div>
                  </div>
                  <div
                     class="ps-4 sm:ps-8 pe-2 col-span-4 text-sm text-left pb-10 border-gray-100 border-opacity-40 border-l"
                  >
                     <div class="mb-2 text-sm font-medium">Therapist Details:</div>
                     <div class="mb-2 text-xs">
                        <p class="text-gray-600">
                           <span class="font-medium text-gray-900"> Name: </span>
                           {{ invoiceDetail?.therapist?.name }}
                        </p>
                     </div>
                     <div v-if="invoiceDetail?.therapist?.license" class="text-xs mb-2">
                        <p class="text-gray-600">
                           <span class="font-medium text-gray-900"> License: </span>
                           {{ invoiceDetail?.therapist?.license || '-' }}
                        </p>
                     </div>
                     <div class="text-xs">
                        <p class="text-gray-600">
                           <span class="font-medium text-gray-900"> Country: </span>
                           {{ invoiceDetail?.therapist?.country || 'UAE' }}
                        </p>
                     </div>
                  </div>
                  <div
                     class="ps-4 sm:ps-8 col-span-4 text-sm border-l border-gray-100 border-opacity-40"
                  >
                     <div class="mb-2 font-medium">Talktime Limited</div>
                     <div class="text-gray-600 pb-6 text-xs">
                        <div class="my-2">Floor No. 11, Al Sarab Tower,</div>
                        Abu Dhabi Global Market Square,
                        <div class="my-2">Al Maryah Island, Abu Dhabi</div>
                        United Arab Emirates
                        <div class="mt-2">TRN: 105077404900003</div>
                     </div>
                  </div>
               </div>
               <div class="py-3 px-4 text-xs border-gray-100 border-opacity-40 border-b">
                  <div class="mb-3 text-sm font-medium">Coverage Information:</div>
                  <ul class="text-xs list-disc ms-4 text-gray-600">
                     <li>
                        <span class="font-medium text-gray-800 text-xs">Coverage Type: </span>
                        {{ invoiceDetail?.coverage?.type || 'Reimbursable Insurance' }}
                     </li>
                     <li class="my-2">
                        <span class="font-medium text-gray-800 text-xs">Insurance Provider: </span>
                        {{
                           invoiceDetail?.coverage?.provider_display_name ||
                           invoiceDetail?.coverage?.provider ||
                           ''
                        }}
                     </li>
                     <li class="mb-3">
                        <span class="font-medium w-28 text-gray-800 text-xs"
                           >Client Concern/s:
                        </span>
                        <span>
                           {{ invoiceDetail?.coverage?.concerns }}
                        </span>
                     </li>
                  </ul>
               </div>

               <div class="mb-2 ms-4 mt-2">
                  <div class="flex justify-between">
                     <div class="py-2 col-span-1 text-[11px] font-medium">
                        Sessions Details
                        <div class="pb-4 pt-2 col-span-1 text-[11px] text-gray-600">
                           {{ invoiceDetail?.session?.name }}
                        </div>
                     </div>
                     <div class="py-2 col-span-1 text-[11px] font-medium">
                        Booking Date
                        <div class="pb-4 pt-2 col-span-1 text-[11px] text-gray-600">
                           {{ dayjs(invoiceDetail?.session?.booking_date).format('DD MMMM, YYYY') }}
                        </div>
                     </div>
                     <div class="py-2 col-span-1 text-[11px] font-medium">
                        Session Date
                        <div class="pb-4 pt-2 col-span-1 text-[11px] text-gray-600">
                           {{ dayjs(invoiceDetail?.session?.session_date).format('DD MMMM, YYYY') }}
                        </div>
                     </div>
                     <div class="py-2 col-span-1 text-[11px] font-medium">
                        Package
                        <div class="pb-4 pt-2 col-span-1 text-[11px] text-gray-600">
                           {{ invoiceDetail?.session?.purchase_from }}
                        </div>
                     </div>
                     <div class="py-2 pe-2 col-span-1 text-[11px] font-medium">
                        Service Price
                        <div class="pb-4 pt-2 col-span-1 text-[11px] text-gray-600">
                           {{ invoiceDetail?.session?.price }}
                        </div>
                     </div>
                  </div>
               </div>
               <div class="flex border-t border-gray-100 border-opacity-40 me-2 pt-2 justify-end">
                  <div class="w-1/3">
                     <div
                        class="flex justify-between pb-2 border-gray-100 border-opacity-40 items-center border-b"
                     >
                        <span class="py-2 font-medium text-xs text-gray-1000 px-2">Subtotal</span>
                        <span class="py-2 px-2 text-xs text-gray-600 text-right">{{
                           invoiceDetail?.summary?.subtotal
                        }}</span>
                     </div>
                     <div
                        class="flex justify-between pb-2 items-center border-gray-100 border-opacity-40 border-b pt-2"
                     >
                        <span class="py-2 font-medium text-xs text-gray-1000 ps-2"
                           >VAT ({{ vatAmount }}%)</span
                        >
                        <span class="py-2 px-2 text-xs text-gray-600 text-right">{{
                           invoiceDetail?.summary?.tax
                        }}</span>
                     </div>
                     <div class="pt-2 flex text-xs justify-between text-primary-600 items-center">
                        <span class="font-medium px-2">Total</span>
                        <span class="pt-2 px-2 text-right">
                           {{ invoiceDetail?.summary?.total }}
                        </span>
                     </div>
                  </div>
               </div>
               <div class="footer">
                  <div
                     class="text-gray-600 ms-4 border-gray-100 border-opacity-40 border-b text-left text-xs"
                  >
                     <p class="mb-2 mt-4 text-[11px]">
                        This invoice has been automatically generated for the individual completed
                        session. The session fee is linked to the Stripe invoice with reference
                        number:
                        <strong> {{ invoiceIdentity.reference }} </strong>. Please note that the Stripe invoice
                        may cover the fee for a single session or a bundle of sessions.
                     </p>
                     <div class="font-medium text-gray-1000 text-sm mt-6">
                        Thank you for trusting Talktime!
                     </div>
                     <CommonIsoStandard align="left" :isStamp="true"></CommonIsoStandard>
                  </div>
                  <div
                     class="py-2 ps-4 overflow-x-scroll text-gray-700 flex text-xs font-medium justify-between"
                  >
                     <div class="text-xs">Talktime, Abu Dhabi</div>
                     <div>+971 4 836 9411 | customercare@talktime.ae</div>
                  </div>
               </div>
            </div>
         </div>
         <div class="mb-4 order-2 col-span-12 sm:col-span-3 text-center">
            <div class="pb-3 px-3 mt-3 shadow-md">
               <div class="my-4">Get a PDF Copy</div>
               <BaseButton
                  @click="downloadPDF"
                  :disabled="!canDownloadInvoice"
                  variant="outlined"
                  color="black"
                  block
                  class="mx-auto"
               >
                  <ArrowDownOnSquareIcon class="h-5 w-8"></ArrowDownOnSquareIcon>
                  <span class="text-gray-1000 text-sm font-medium ms-2"> Download PDF </span>
               </BaseButton>
            </div>
            <div
               class="my-6 border bg-green-500 bg-opacity-5 rounded-md text-left p-3 text-xs border-green-500 text-green-500 items-start flex gap-3"
            >
               <CheckCircleIcon class="h-10 w-10 -mt-2 text-green-500"></CheckCircleIcon>
               <div>You are eligible to reimburse through your Insurance provider.</div>
            </div>
            <!-- <div class="text-left">
               <ClientReimbursableGuideDialog></ClientReimbursableGuideDialog>
            </div> -->
         </div>
      </div>
   </div>
</template>
<script setup>
   import html2pdf from 'html2pdf.js';
   import { ArrowLeftIcon, ArrowRightIcon, ArrowDownOnSquareIcon } from '@heroicons/vue/20/solid';
   import dayjs from 'dayjs';
   const { locale } = useI18n();
   import { CheckCircleIcon } from '@heroicons/vue/24/outline';
   import {
      canExportReimbursableInvoice,
      formatReimbursableInvoiceIdentity,
   } from '~/utils/reimbursable_invoice';
   const emit = defineEmits(['show-list']);
   const { invoiceDetail } = storeToRefs(useReimbursableStore());
   const invoiceIdentity = computed(() => formatReimbursableInvoiceIdentity(invoiceDetail.value));
   const canDownloadInvoice = computed(() => canExportReimbursableInvoice(invoiceDetail.value));
   const vatAmount = computed(() => {
      return invoiceDetail?.value?.summary?.tax_percentage;
   });
   const downloadPDF = () => {
      if (!canDownloadInvoice.value) return;
      const element = document.getElementById('invoice-content');
      if (!element) return;
      const opt = {
         margin: 0.2,
         filename: 'invoice.pdf',
         image: { type: 'jpeg', quality: 0.98 },
         html2canvas: {
            scale: 2,
         },
         jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
      };

      // Add class for PDF styling
      element.classList.add('pdf-view-active');

      html2pdf()
         .set(opt)
         .from(element)
         .save()
         .then(() => {
            // Remove class after PDF generation
            element.classList.remove('pdf-view-active');
         })
         .catch(() => {
            // Ensure class is removed even if there's an error
            element.classList.remove('pdf-view-active');
         });
   };
</script>
<style lang="css" scoped>
   ul li {
      list-style-type: none;
      &:before {
         content: '• ';
         margin-left: -1em;
         font-size: 18px;
      }
   }
   #invoice-content.pdf-view-active {
      display: flex;
      flex-direction: column;
      /* A4 paper height (11.69in) minus 0.2in margin top & bottom for html2pdf = 11.29in */
      min-height: 11.29in;
   }
</style>
